import React, { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import Avatar from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import toast from 'react-hot-toast';
import { User as UserIcon, Mail, Briefcase, Building2, Calendar, UploadCloud, Save, Camera, Trash2, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function ProfilePage() {
  const { user, updateUser } = useAuthStore();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isPhotoMenuOpen, setIsPhotoMenuOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsPhotoMenuOpen(false);
      }
    };
    if (isPhotoMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPhotoMenuOpen]);

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await userApi.getMe();
      return res.data.data;
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: any) => userApi.updateProfile(data),
    onSuccess: (res: any) => {
      toast.success('Profile updated successfully');
      updateUser(res.data.data);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    }
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) => userApi.uploadPhoto(id, formData),
    onSuccess: (res: any) => {
      toast.success('Photo updated successfully');
      updateUser(res.data.data);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update photo');
    }
  });

  const removePhotoMutation = useMutation({
    mutationFn: (id: string) => userApi.removePhoto(id),
    onSuccess: () => {
      toast.success('Photo removed successfully');
      const updatedUser = { ...user!, photoUrl: undefined };
      updateUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to remove photo');
    }
  });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profileData?.user?.id) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('photo', file);
    uploadPhotoMutation.mutate({ id: profileData.user.id, formData });
    
    // reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProfileSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateProfileMutation.mutate({
      name: formData.get('name'),
      designation: formData.get('designation'),
      department: formData.get('department'),
    });
  };

  if (isLoading || !profileData) {
    return <div className="animate-pulse h-96 bg-slate-100 rounded-2xl"></div>;
  }

  const { user: profile, balances } = profileData;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      
      {/* Cover & Avatar Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 relative">
        <div className="h-48 gradient-brand w-full relative overflow-hidden rounded-t-2xl">
          <div className="absolute top-[-20%] left-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute right-[-5%] bottom-[-50%] w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
        </div>
        
        <div className="px-6 sm:px-10 pb-8 relative">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 -mt-16 sm:-mt-20 mb-6">
            <div className="flex items-end gap-5 relative z-10">
              <div className="relative group">
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-white shadow-lg overflow-hidden shrink-0">
                  <Avatar name={profile.name} photoUrl={profile.photoUrl} size="xl" className="w-full h-full" />
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  accept="image/jpeg, image/png, image/webp" 
                  onChange={handlePhotoChange} 
                />
                <button 
                  type="button"
                  onClick={() => setIsPhotoMenuOpen(!isPhotoMenuOpen)}
                  disabled={uploadPhotoMutation.isPending || removePhotoMutation.isPending}
                  className={`absolute inset-0 bg-black/50 rounded-full flex flex-col items-center justify-center text-white transition-opacity disabled:opacity-100 ${isPhotoMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                >
                  <Camera className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium">
                    {uploadPhotoMutation.isPending || removePhotoMutation.isPending ? 'Updating...' : 'Change Photo'}
                  </span>
                </button>

                {isPhotoMenuOpen && (
                  <div ref={menuRef} className="absolute top-full mt-3 left-0 w-56 bg-slate-900 rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200 border border-slate-800">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                      <span className="text-sm font-semibold text-slate-200">Change profile picture</span>
                      <button onClick={() => setIsPhotoMenuOpen(false)} className="text-slate-400 hover:text-slate-200 transition-colors p-1 rounded-full hover:bg-slate-800">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-1.5">
                      <button
                        onClick={() => { fileInputRef.current?.click(); setIsPhotoMenuOpen(false); }}
                        className="w-full text-left px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-800 rounded-lg flex items-center gap-3 transition-colors font-medium"
                      >
                        <UploadCloud className="w-4 h-4 text-slate-400" /> Upload from device
                      </button>
                      {profile.photoUrl && (
                        <button
                          onClick={() => { removePhotoMutation.mutate(profile.id); setIsPhotoMenuOpen(false); }}
                          className="w-full text-left px-3 py-2.5 text-sm text-red-400 hover:bg-slate-800 rounded-lg flex items-center gap-3 transition-colors font-medium mt-0.5"
                        >
                          <Trash2 className="w-4 h-4" /> Remove photo
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="pb-2">
                <h1 className="text-3xl font-bold text-slate-800">{profile.name}</h1>
                <p className="text-slate-500 font-medium">{profile.designation}</p>
              </div>
            </div>


          </div>

          {/* Quick Info Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Department</p>
                <p className="font-medium text-slate-800">{profile.department}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Employee ID</p>
                <p className="font-medium text-slate-800">{profile.employeeId}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Joined Date</p>
                <p className="font-medium text-slate-800">{format(parseISO(profile.dateOfJoining), 'MMM dd, yyyy')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Manager</p>
                <p className="font-medium text-slate-800">{profile.manager?.name || 'None'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column - Forms */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 animate-fade-in">
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-4">
                <UserIcon className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-800">Basic Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Full Name" name="name" defaultValue={profile.name} required />
                <Input label="Email Address" defaultValue={profile.email} disabled helperText="Contact IT to change your email" leftIcon={<Mail className="w-4 h-4" />} />
                <Input label="Designation" name="designation" defaultValue={profile.designation || ''} placeholder="e.g. Senior Developer" />
                <Input label="Department" name="department" defaultValue={profile.department || ''} placeholder="e.g. Engineering" />
              </div>

              <div className="pt-4 flex justify-end border-t border-slate-100">
                <Button type="submit" isLoading={updateProfileMutation.isPending}>
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column - Mini Balances */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-6">Leave Summary</h2>
            <div className="space-y-4">
              {balances.map((balance: any) => (
                <div key={balance.id} className="p-4 rounded-xl border border-slate-100 bg-slate-50 hover:border-slate-200 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: balance.leaveType.colour }}></div>
                      <span className="font-semibold text-slate-700">{balance.leaveType.name}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-500">{balance.leaveType.code}</span>
                  </div>
                  <div className="flex items-end gap-2 mb-3">
                    <span className="text-3xl font-black text-slate-800 tracking-tight leading-none">{balance.available}</span>
                    <span className="text-xs font-medium text-slate-500 mb-0.5">days available</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                    <div 
                      className="h-1.5 rounded-full" 
                      style={{ 
                        width: `${Math.min(100, balance.utilisation)}%`,
                        backgroundColor: balance.leaveType.colour
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
